/**
 * The one-time card over the grid: connect Google Calendar, or „Nie teraz" (owner, 24 October
 * 2026); since 29 October 2026 also „Dodaj do Kalendarza Apple" on an Apple device — one calendar
 * or the other. No handoff — the booking tabs' own card and type, and its dialogs' buttons. R2:
 * what the Google grant is for is said here, before the consent page opens, whenever it is offered.
 */

import { useTranslation } from 'react-i18next';

import surface from '~/shared/ui/dialogSurface.module.css';
import { FormBanner } from '~/shared/ui/FormBanner';
import ink from '~/shared/ui/inkButton.module.css';

import type { GoogleCalendarPromptApi } from '../lib/useGoogleCalendarPrompt';
import frame from './bookingTab.module.css';
import styles from './CalendarTab.module.css';
import schedule from './ScheduleTab.module.css';

const TITLE_ID = 'booking-google-prompt-title';

export function GoogleCalendarPrompt({ prompt }: { readonly prompt: GoogleCalendarPromptApi }) {
  const { t } = useTranslation();
  const { google, apple } = prompt.options;
  // Google alone keeps its own words; with Apple on offer the card speaks of either calendar.
  const copy = apple ? 'web:booking.calendarPrompt' : 'web:booking.googlePrompt';
  const problem = prompt.connectFailed
    ? t('web:account.calendar.connectFailed')
    : prompt.appleProblem === null
      ? null
      : t(`web:account.calendar.apple.${prompt.appleProblem}`);

  return (
    <section
      className={`${String(frame.card)} ${String(styles.prompt)} ${prompt.reveal}`}
      aria-labelledby={TITLE_ID}
      data-google-prompt=""
    >
      <div className={styles.promptRow}>
        <div className={styles.promptWords}>
          <h2 id={TITLE_ID} className={frame.cardTitle}>
            {t(`${copy}.title`)}
          </h2>
          <p className={schedule.cardIntro}>{t(`${copy}.body`)}</p>
          {google ? <p className={schedule.cardIntro}>{t('web:account.calendar.disclosure')}</p> : null}
          <p className={schedule.cardIntro}>{t('web:booking.googlePrompt.hint')}</p>
        </div>
        {/* The dialogs' pair (`BlockDialog`, `CopyDayDialog`), the primaries in ink like /konto's buttons. */}
        <div className={surface.actions}>
          <button type="button" className={surface.ghost} onClick={prompt.onNotNow}>
            {t('web:booking.googlePrompt.notNow')}
          </button>
          {apple ? (
            <button
              type="button"
              className={`${String(ink.ink)} ${String(styles.promptConnect)}`}
              aria-disabled={prompt.subscribing ? 'true' : undefined}
              aria-busy={prompt.subscribing}
              onClick={prompt.subscribing ? undefined : prompt.onApple}
            >
              {t('web:account.calendar.apple.add')}
            </button>
          ) : null}
          {google ? (
            <button
              type="button"
              className={`${String(ink.ink)} ${String(styles.promptConnect)}`}
              aria-disabled={prompt.connecting ? 'true' : undefined}
              aria-busy={prompt.connecting}
              onClick={prompt.connecting ? undefined : prompt.onConnect}
            >
              {prompt.connecting
                ? t('web:account.calendar.connecting')
                : t('web:account.calendar.connect')}
            </button>
          ) : null}
        </div>
      </div>
      <FormBanner message={problem} />
    </section>
  );
}
